import React, { useEffect, useState } from "react";
import { Smartphone, Download, Bell, X, ChevronRight } from "lucide-react";

const DISMISS_KEY = "tigik_install_banner_dismissed_v2";
const DISMISS_DAYS = 14;

function isIOS() {
  return /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;
}
function isAndroid() {
  return /android/i.test(navigator.userAgent);
}
function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)").matches
    || window.navigator.standalone === true;
}
function dismissedRecently() {
  const v = localStorage.getItem(DISMISS_KEY);
  if (!v) return false;
  const days = (Date.now() - parseInt(v, 10)) / 86400000;
  return days < DISMISS_DAYS;
}

/**
 * Smart install + push notifications onboarding banner.
 * - Shows only on mobile (iOS/Android)
 * - Hidden if installed as PWA, or dismissed within last 14 days
 */
export default function InstallBanner() {
  const [step, setStep] = useState(null); // null | 'intro' | 'ios-steps' | 'android-steps' | 'notifications'

  useEffect(() => {
    if (isStandalone() || dismissedRecently()) return;
    if (!isIOS() && !isAndroid()) return;
    // small delay so the banner doesn't blink during route load
    // Show after the visitor has seen the hero: on scroll or after 20 s.
    const open = () => setStep((st) => st || "intro");
    const onScroll = () => { if (window.scrollY > 600) open(); };
    window.addEventListener("scroll", onScroll, { passive: true });
    const t = setTimeout(open, 20000);
    return () => { clearTimeout(t); window.removeEventListener("scroll", onScroll); };
  }, []);

  const close = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setStep(null);
  };

  const requestNotifs = async () => {
    if (typeof Notification === "undefined") {
      alert("Tvoj prehliadač nepodporuje notifikácie.");
      return;
    }
    const r = await Notification.requestPermission();
    if (r === "granted") {
      try {
        new Notification("Tigik 🎉", {
          body: "Notifikácie sú zapnuté! Príď neskôr pozrieť či ti niekto napísal.",
          icon: "/tigik-icon-192.png?v=2.5",
        });
      } catch (_) { /* ignore */ }
      close();
    } else {
      alert("Notifikácie zostali vypnuté. Môžeš ich zapnúť neskôr v nastaveniach prehliadača.");
      close();
    }
  };

  if (!step) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 p-3 pb-[max(env(safe-area-inset-bottom),12px)]" data-testid="install-banner">
      <div className="max-w-md mx-auto bg-gradient-to-br from-[#1a1408] to-[#0a0805] border border-yellow-600/50 shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-yellow-700/30">
          <div className="flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-yellow-400" />
            <span className="font-display font-bold text-sm text-white">
              {step === "intro" && "Pridaj Tigik na plochu"}
              {step === "ios-steps" && "iPhone — návod (3 kroky)"}
              {step === "android-steps" && "Android — návod (3 kroky)"}
              {step === "notifications" && "Posledný krok 🔔"}
            </span>
          </div>
          <button onClick={close} className="text-zinc-500 hover:text-white" data-testid="install-banner-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 py-4 space-y-3">
          {step === "intro" && (
            <>
              <p className="text-sm text-zinc-300 leading-relaxed">
                Tigik môžeš pridať na <strong className="text-yellow-400">plochu telefónu</strong> ako aplikáciu —
                rýchlejšie otvorenie + budú ti chodiť <strong className="text-yellow-400">notifikácie</strong> z chatu.
              </p>
              <div className="bg-zinc-900/60 border border-yellow-700/20 p-3 text-xs text-zinc-400 font-mono leading-relaxed">
                ⚠️ <strong className="text-amber-400">Dôležité:</strong> ak chceš upozornenia na chat,
                <strong className="text-white"> nechaj appku spustenú na pozadí</strong> (nezatváraj ju úplne).
                Bez toho ti telefón nezobrazí banner s novou správou.
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => setStep(isIOS() ? "ios-steps" : "android-steps")}
                  data-testid="install-banner-show-steps"
                  className="wb-btn-primary flex-1 py-2.5 text-xs inline-flex items-center justify-center gap-1"
                >
                  <Download className="w-3.5 h-3.5" /> Návod pridať na plochu
                </button>
                <button onClick={close} className="wb-btn-ghost px-3 py-2.5 text-xs">Neskôr</button>
              </div>
            </>
          )}

          {step === "ios-steps" && (
            <>
              <ol className="space-y-2 text-sm text-zinc-300">
                <li className="flex gap-2">
                  <span className="w-5 h-5 rounded-full bg-yellow-500 text-black font-bold text-xs flex items-center justify-center shrink-0">1</span>
                  <span>Klikni na <strong>tlačidlo zdielania</strong> v Safari (šípka v rámčeku 📤 dole)</span>
                </li>
                <li className="flex gap-2">
                  <span className="w-5 h-5 rounded-full bg-yellow-500 text-black font-bold text-xs flex items-center justify-center shrink-0">2</span>
                  <span>Vyber <strong>„Add to Home Screen / Pridať na úvodnú obrazovku"</strong></span>
                </li>
                <li className="flex gap-2">
                  <span className="w-5 h-5 rounded-full bg-yellow-500 text-black font-bold text-xs flex items-center justify-center shrink-0">3</span>
                  <span>Klikni <strong>„Pridať"</strong> v pravom hornom rohu</span>
                </li>
              </ol>
              <button onClick={() => setStep("notifications")} data-testid="install-banner-next"
                className="wb-btn-primary w-full py-2.5 text-xs inline-flex items-center justify-center gap-1">
                Mám to → ďalší krok <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </>
          )}

          {step === "android-steps" && (
            <>
              <ol className="space-y-2 text-sm text-zinc-300">
                <li className="flex gap-2">
                  <span className="w-5 h-5 rounded-full bg-yellow-500 text-black font-bold text-xs flex items-center justify-center shrink-0">1</span>
                  <span>Klikni na <strong>menu Chrome</strong> (3 bodky vpravo hore)</span>
                </li>
                <li className="flex gap-2">
                  <span className="w-5 h-5 rounded-full bg-yellow-500 text-black font-bold text-xs flex items-center justify-center shrink-0">2</span>
                  <span>Vyber <strong>„Pridať na plochu / Install app"</strong></span>
                </li>
                <li className="flex gap-2">
                  <span className="w-5 h-5 rounded-full bg-yellow-500 text-black font-bold text-xs flex items-center justify-center shrink-0">3</span>
                  <span>Potvrď <strong>„Pridať / Install"</strong></span>
                </li>
              </ol>
              <button onClick={() => setStep("notifications")} data-testid="install-banner-next"
                className="wb-btn-primary w-full py-2.5 text-xs inline-flex items-center justify-center gap-1">
                Mám to → ďalší krok <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </>
          )}

          {step === "notifications" && (
            <>
              <div className="flex items-center gap-2 text-yellow-400">
                <Bell className="w-5 h-5" />
                <span className="font-display font-bold text-sm">Zapni upozornenia</span>
              </div>
              <p className="text-sm text-zinc-300 leading-relaxed">
                Aby ti chodili notifikácie keď ti niekto napíše v chate alebo ťa označí cez <strong className="text-yellow-400">@meno</strong>:
              </p>
              <ol className="space-y-1.5 text-xs text-zinc-400 list-decimal list-inside">
                <li>Klikni dole na tlačidlo <strong className="text-yellow-400">„Povoliť notifikácie"</strong></li>
                <li>Prehliadač sa spýta → daj <strong>„Povoliť / Allow"</strong></li>
                <li>Nechaj Tigik spustený na pozadí v telefóne</li>
              </ol>
              <div className="flex gap-2 pt-1">
                <button onClick={requestNotifs} data-testid="install-banner-enable-notif"
                  className="wb-btn-primary flex-1 py-2.5 text-xs inline-flex items-center justify-center gap-1">
                  <Bell className="w-3.5 h-3.5" /> Povoliť notifikácie
                </button>
                <button onClick={close} className="wb-btn-ghost px-3 py-2.5 text-xs">Hotovo</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
